import { ApolloCache, InMemoryCache } from '@apollo/client';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { ApptNotificationActionType, isApptNotificationChannel } from '@components/notifications/utils';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Notification } from '@components/mySchedule/types';
import { AgendaDate } from '@graphql/gql/graphql';
import { AGENDA_DATES } from '@graphql/operations/schedule/agendaDates';

const LOG = LoggerFactory.create('updateAgendaDates');

const compareAgendaDates = (firstAgendaDate: AgendaDate, secondAgendaDate: AgendaDate): number => {
  if (firstAgendaDate.agendaDate < secondAgendaDate.agendaDate) {
    return -1;
  }
  if (firstAgendaDate.agendaDate > secondAgendaDate.agendaDate) {
    return 1;
  }
  return 0;
};

/**
 * Merge the new agenda date with existing agenda dates
 */
const getUpdatedAgendaDates = (existingAgendaDates: AgendaDate[], newAgendaDate: string): AgendaDate[] => {
  const updatedAgendaDates = [...existingAgendaDates];
  if (!updatedAgendaDates.find(agendaDate => agendaDate.agendaDate === newAgendaDate)) {
    updatedAgendaDates.push({
      agendaDate: newAgendaDate,
      __typename: 'AgendaDate'
    });
    updatedAgendaDates.sort(compareAgendaDates);
  }

  return updatedAgendaDates;
};

const UPSERT_ACTION_TYPES = new Set<string>([ApptNotificationActionType.UPDATE, ApptNotificationActionType.INVITE]);

/**
 * Determine if AGENDA_DATES query cache needs to be updated
 * @param notifications
 */
function shouldUpdateAgendaDates(notifications: Notification[]): boolean {
  return (
    notifications.length === 1 &&
    isApptNotificationChannel(notifications[0].channelName) &&
    UPSERT_ACTION_TYPES.has(notifications[0].metadata.entity.action)
  );
}

const getAgendaDate = (agendaTime, preferredTimezone): string => {
  return format(utcToZonedTime(agendaTime, preferredTimezone), 'yyyy-MM-dd');
};

export const updateAgendaDatesFromNotification = (
  cache: InMemoryCache,
  notifications: Notification[],
  preferredTimezone: string
) => {
  if (!cache || !notifications) {
    return;
  }

  if (shouldUpdateAgendaDates(notifications)) {
    const newAgendaDate = getAgendaDate(notifications[0]?.metadata?.entity?.startTime, preferredTimezone);

    updateAgendaDates(cache, newAgendaDate, preferredTimezone);
  }
};

export function updateAgendaDates(
  cache: InMemoryCache | ApolloCache<object>,
  newAgendaDate: string,
  preferredTimezone: string
) {
  if (!newAgendaDate || !cache) {
    return;
  }

  // get current agenda dates
  const agendaDates = cache.readQuery({
    query: AGENDA_DATES,
    variables: {
      params: {
        preferredTimezone
      }
    }
  });

  if (agendaDates) {
    const updatedAgendaDates = getUpdatedAgendaDates(agendaDates.getAgendaDates.data || [], newAgendaDate);

    const success = cache.writeQuery({
      query: AGENDA_DATES,
      variables: {
        params: {
          preferredTimezone
        }
      },
      data: {
        ...agendaDates,
        getAgendaDates: {
          ...agendaDates.getAgendaDates,
          data: [...updatedAgendaDates]
        }
      },
      overwrite: true
    });

    if (!success) {
      LOG.warn('Failed to update agenda dates cache');
    }
  }
}
